import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { Coordinates } from "@/custom-types/Coordinates";
import { Location } from "@/custom-types/Location";
import {
  LOAD_COOKIES,
  type LoadCookiesAction,
} from "@/redux/action-creators/cookies";
import { HYDRATE } from "@/redux/reducers/hydration";
import logError from "@/utils/logError";

export type LocationState = {
  coordinates: Coordinates;
  countryCode: string;
  city: string;
  county?: string;
  defaultLocation: boolean;
  deliveryAddress?: Location;
  formattedLocation: string;
  formattedAddress?: string;
  geoSlug: string;
  isUserLocation?: boolean;
  locationLoadedFromCookie: boolean;
  requestingLocation: boolean;
  place_id?: string;
  state: string;
  state_code?: string;
  stateCode?: string;
  street?: {
    number: string;
    name: string;
  };
  zip: string;
};

export const initialState: LocationState = {
  city: "Seattle",
  coordinates: {
    lat: 47.6062,
    lon: -122.3321,
  },
  countryCode: "US",
  defaultLocation: true,
  deliveryAddress: {},
  formattedLocation: "Seattle, WA",
  geoSlug: "seattle-wa-us",
  locationLoadedFromCookie: false,
  requestingLocation: false,
  state: "Washington",
  state_code: "WA",
  zip: "98164",
};

// @ts-ignore (fix me please, do not replicate)
const mergeCoordinates = (coordinates): Coordinates => ({
  lat: coordinates?.lat || coordinates?.latitude || 0,
  lon: coordinates?.lon || coordinates?.longitude || 0,
});

const formatAddress = (
  streetNumber: string,
  streetName: string,
  city: string,
  state: string,
) => {
  if (!!streetNumber && !!streetName && !!city && !!state) {
    return `${streetNumber} ${streetName}, ${city}, ${state}`;
  }
};

// @ts-ignore (fix me please, do not replicate)
export const mergeLocationProperties = (location) => {
  return {
    addressLine1: location?.addressLine1 || "",
    addressLine2: location?.addressLine2 || "",
    city: location?.city || "",
    coordinates: mergeCoordinates(location?.coordinates || location?.location),
    country:
      location?.country ||
      location?.countryCode ||
      location?.country_code ||
      "",
    countryCode: location?.countryCode || location?.country_code || "",
    ...(location?.county ? { county: location?.county } : {}),
    formattedAddress:
      location?.formattedAddress ||
      formatAddress(
        location?.street?.number,
        location?.street?.name,
        location?.city,
        location?.state,
      ),
    formattedLocation:
      location?.formattedLocation || location?.formatted_location || "",
    geoSlug: location?.slug || location?.geoSlug || "",
    isUserLocation: location?.isUserLocation,
    place_id: location?.place_id || location?.placeId,
    state: location?.state || "",
    state_code: location?.state_code,
    street: location?.street,
    zip: location?.zip || "",
  };
};

const locationSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state) => {
      /**
       * Ignore any hydration events after initial page load to preserve any
       * subsequent changes that have been made on the client.
       */
      return state;
    });

    builder.addCase(LOAD_COOKIES, (state, action: LoadCookiesAction) => {
      const leaflyLocationCookie =
        action.cookies && action.cookies["leafly-location"];
      if (leaflyLocationCookie) {
        let location;

        try {
          location = JSON.parse(decodeURIComponent(leaflyLocationCookie));
        } catch {
          logError("Unable to parse location cookie in locationSlice");
        }

        if (location?.coordinates?.latitude) {
          Object.assign(state, {
            ...mergeLocationProperties(location),
            defaultLocation: false,
            locationLoadedFromCookie: true,
            street: location.street,
          });
          return;
        }
      }

      state.locationLoadedFromCookie = !!leaflyLocationCookie;
    });
  },
  initialState,
  name: "location",
  reducers: {
    cancelRequestLocation: (state) => {
      state.requestingLocation = false;
    },
    loadLocation: (state, action: PayloadAction<Location>) => {
      if (action.payload) {
        const location = action.payload;
        Object.assign(state, {
          ...mergeLocationProperties(location),
          defaultLocation: false,
        });
      }
    },
    requestLocation: (state) => {
      state.requestingLocation = true;
    },
    setUserDeliveryAddress: (state, action: PayloadAction<Location>) => {
      state.deliveryAddress = {
        addressLine1: action.payload.addressLine1 || "",
        addressLine2: action.payload.addressLine2 || "",
        city: action.payload.city || "",
        country: action.payload.country || "",
        countryCode: action.payload.countryCode || "",
        ...(action.payload.county ? { county: action.payload.county } : {}),
        formattedAddress: action.payload.formattedAddress || "",
        latitude: action.payload.latitude,
        longitude: action.payload.longitude,
        postalCode: action.payload.postalCode,
        state: action.payload.state || "",
        state_code: action.payload.state_code || "",
        street: {
          name: (action.payload.street || {}).name || "",
          number: (action.payload.street || {}).number || "",
        },
      };
    },
  },
});

export const {
  loadLocation,
  requestLocation,
  cancelRequestLocation,
  setUserDeliveryAddress,
} = locationSlice.actions;

export default locationSlice.reducer;
